// public/js/services/analysis/GameAnalysisService.js
import { CoreAnalysisMethods } from './CoreAnalysisMethods.js';
import { AnalysisComponentMethods } from './AnalysisComponentMethods.js';
import { TrendAnalysisMethods } from './TrendAnalysisMethods.js';
import { RecommendationMethods } from './RecommendationMethods.js';
import { UtilityMethods } from './UtilityMethods.js';
import { unwrapWishlistResponse } from '../../utils/wishlistUtils.js';

export class GameAnalysisService {
    constructor(options = {}) {
        this.cache = new Map();
        this.abortController = new AbortController();
        this.options = {
            cacheDuration: 3600000, // 1 hour
            maxRetries: 3,
            batchSize: 50,
            ...options
        };

        // Mix in all method groups
        Object.assign(
            this,
            CoreAnalysisMethods,
            AnalysisComponentMethods,
            TrendAnalysisMethods,
            RecommendationMethods,
            UtilityMethods
        );
    }

    // Cache Methods
    generateCacheKey(games) {
        return games
            .map(g => g.steamId)
            .sort()
            .join('-');
    }

    getCachedResults(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.options.cacheDuration) {
            return cached.data;
        }
        return null;
    }

    setCachedResults(key, data) {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    // API Methods
    async fetchGameDetails(steamId) {
        const response = await fetch(`/analytics/api/game/${steamId}`);
        if (!response.ok) throw new Error('Failed to fetch game details');
        return response.json();
    }

    async fetchWishlistData(steamId) {
        const response = await fetch(`/analytics/api/game/${steamId}/wishlist-insights`);
        if (!response.ok) throw new Error('Failed to fetch wishlist data');
        const payload = await response.json();
        const data = unwrapWishlistResponse(payload);
        if (!data) {
            throw new Error('Invalid wishlist data payload');
        }
        return data;
    }

    // Control Methods
    cancelAnalysis() {
        this.abortController.abort();
        this.abortController = new AbortController();
    }

    clearCache() {
        this.cache.clear();
    }
}
